import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Phone, MessageCircle, Mail, Share2, Building2 } from 'lucide-react';
import baseurl from '../Baseurl/baseurl';
import { Card, Heading, Eyebrow, Avatar, Loader, EmptyState, Divider } from '../ui';

const BusinessDetailView = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loggedInMember, setLoggedInMember] = useState(null);
    const [chipFilter, setChipFilter] = useState('');
    const [categories, setCategories] = useState({});

    useEffect(() => {
        const stored = JSON.parse(localStorage.getItem('memberData'));
        const token = localStorage.getItem('token');

        if (stored?.mid && token) {
            fetch(`${baseurl}/api/member/${stored.mid}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.success && data.data) {
                        setLoggedInMember(data.data);
                    } else {
                        setLoggedInMember(stored);
                    }
                })
                .catch(() => setLoggedInMember(stored));
        }
    }, []);

    // Fetch all categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${baseurl}/api/category/all`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    // Create a mapping of category IDs to category names
                    const categoriesMap = {};
                    data.data.forEach(category => {
                        categoriesMap[category.cid] = category.category_name;
                    });
                    setCategories(categoriesMap);
                }
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };

        fetchCategories();
    }, []);

    // Set chipFilter based on member status
    useEffect(() => {
        if (loggedInMember) {
            if (loggedInMember.status === 'Pending') {
                setChipFilter('Business Type');
            } else {
                setChipFilter('All');
            }
        }
    }, [loggedInMember]);

    useEffect(() => {
        const fetchMemberData = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${baseurl}/api/member/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    setMember(data.data);
                }
            } catch (err) {
                console.error('Error fetching member:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchMemberData();
    }, [id]);

    const handleShare = () => {
        const primaryBusiness = member?.BusinessProfiles?.[0];
        if (primaryBusiness && navigator.share) {
            navigator
                .share({
                    title: primaryBusiness.company_name,
                    text: `Check out ${primaryBusiness.company_name}`,
                    url: window.location.href
                })
                .catch((error) => console.error('Error sharing:', error));
        } else {
            alert('Sharing is not supported on this device.');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-bg">
                <Loader label="Loading business details…" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bg pb-24 text-text">
            <div className="mx-auto w-full max-w-[520px] px-4 pt-4">
                {/* Header */}
                <div className="on-dark relative mb-6 overflow-hidden rounded-lg bg-navy px-4 py-4">
                    <div className="pointer-events-none absolute inset-0 bg-line/10" />
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                aria-label="Go back"
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-text transition-colors hover:bg-line/15 hover:text-ink"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div>
                                <Eyebrow>KBC Directory</Eyebrow>
                                <p className="mt-1 font-display text-base font-semibold text-ink">
                                    {t('Business Details')}
                                </p>
                            </div>
                        </div>
                        <Avatar
                            src={
                                loggedInMember?.profile_image
                                    ? `${baseurl}/${loggedInMember.profile_image}`
                                    : undefined
                            }
                            name={loggedInMember?.first_name}
                            size="md"
                        />
                    </div>
                </div>

                {/* Business Profiles */}
                {!member || !Array.isArray(member.BusinessProfiles) || member.BusinessProfiles.length === 0 ? (
                    <EmptyState
                        icon={<Building2 className="h-6 w-6" />}
                        title="No business details"
                        description="No business details found for this member."
                    />
                ) : (
                    <div className="space-y-6 text-left">
                        {member.BusinessProfiles.map((profile, index) => {
                            // Common fields for all business types
                            const commonDetails = [
                                { label: 'Business Name', value: profile.company_name },
                                { label: 'Business Type', value: profile.business_type },
                                { label: 'Category', value: categories[profile.category_id] || 'Loading...' },
                                { label: 'Experience', value: profile.experience },
                                { label: 'Business Work Contract', value: profile.business_work_contract },
                                { label: 'Tags', value: profile.tags },
                                { label: 'Website', value: profile.website },
                                { label: 'Google Link', value: profile.google_link },
                                { label: 'Facebook Link', value: profile.facebook_link },
                                { label: 'Instagram Link', value: profile.instagram_link },
                                { label: 'LinkedIn Link', value: profile.linkedin_link }
                            ];

                            // Business type specific fields
                            const businessTypeSpecificDetails = [];

                            if (profile.business_type === 'salary') {
                                businessTypeSpecificDetails.push(
                                    { label: 'Designation', value: profile.designation },
                                    { label: 'Salary', value: profile.salary },
                                    { label: 'Location', value: profile.location }
                                );
                            } else {
                                businessTypeSpecificDetails.push(
                                    { label: 'Registration Type', value: profile.business_registration_type },
                                    { label: 'Staff Size', value: profile.staff_size },
                                    { label: 'Business Starting Year', value: profile.business_starting_year },
                                    { label: 'Company Address', value: `${profile.company_address},\n${profile.city}, ${profile.state} ${profile.zip_code}` }
                                );
                            }

                            // Additional fields
                            const additionalDetails = [
                                { label: 'About', value: profile.about, fullWidth: true },
                                { label: 'Your Role', value: profile.role },
                                { label: 'Contact', value: profile.contact },
                                { label: 'Email', value: profile.email },
                                { label: 'Source', value: profile.source }
                            ];

                            const details = [...commonDetails, ...businessTypeSpecificDetails, ...additionalDetails];

                            const mediaGallery = profile.media_gallery
                                ? profile.media_gallery.split(',').map((item) => item.trim())
                                : [];

                            return (
                                <Card key={index} padding="lg">
                                    <Heading variant="subtitle" as="h2" className="mb-1">
                                        Business <em className="kbc-serif">Info</em> #{index + 1}
                                    </Heading>

                                    <Divider className="mb-4" />

                                    {/* Business Profile Image */}
                                    {profile.business_profile_image && (
                                        <div className="mb-4 text-center">
                                            <img
                                                src={`${baseurl}/${profile.business_profile_image}`}
                                                alt="Business"
                                                className="mx-auto max-h-[300px] max-w-full rounded-md border border-line/15"
                                            />
                                        </div>
                                    )}

                                    {/* Business Details */}
                                    <div className="space-y-3">
                                        {details.map(({ label, value, fullWidth }) => (
                                            value && (
                                                fullWidth ? (
                                                    // Special layout for About field
                                                    <div key={label} className="mb-2">
                                                        <p className="mb-1 font-semibold text-muted">
                                                            {label}
                                                        </p>
                                                        <p className="text-left leading-relaxed text-text">
                                                            {value || 'N/A'}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    // Standard layout for other fields
                                                    <div
                                                        key={label}
                                                        className="flex items-start justify-between gap-4 leading-relaxed"
                                                    >
                                                        <span className="w-1/2 text-base text-muted">
                                                            {label}
                                                        </span>
                                                        <span
                                                            className={`w-1/2 break-words text-right text-base [overflow-wrap:anywhere] ${
                                                                ['Business Name', 'Business Type', 'Your Role', 'Company Address'].includes(label)
                                                                    ? 'font-semibold text-ink'
                                                                    : 'text-text'
                                                            }`}
                                                        >
                                                            {value || 'N/A'}
                                                        </span>
                                                    </div>
                                                )
                                            )
                                        ))}
                                    </div>

                                    {/* Media Gallery */}
                                    {mediaGallery.length > 0 && (
                                        <div className="mt-6">
                                            <p className="mb-2 font-semibold text-ink">
                                                Media Gallery:
                                            </p>
                                            <div className="space-y-2">
                                                {mediaGallery.map((media, i) => {
                                                    const isVideo = media.endsWith('.mp4');
                                                    return isVideo ? (
                                                        <video
                                                            key={i}
                                                            controls
                                                            className="w-1/2 rounded-md border border-line/15 bg-navy text-center"
                                                        >
                                                            <source src={`${baseurl}/${media}`} type="video/mp4" />
                                                            Your browser does not support the video tag.
                                                        </video>
                                                    ) : (
                                                        <img
                                                            key={i}
                                                            src={`${baseurl}/${media}`}
                                                            alt={`media-${i}`}
                                                            className="w-1/2 rounded-md border border-line/15 bg-surface2"
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Bottom Bar */}
            {member?.BusinessProfiles?.length > 0 && (
                <div className="fixed inset-x-0 bottom-0 z-[999] flex justify-center border-t border-line/15 bg-surface/90">
                    <div className="flex w-full max-w-[520px] justify-around px-4 py-2">
                        <a
                            href={`tel:${member.BusinessProfiles[0]?.contact || ''}`}
                            aria-label="Call"
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/10 hover:text-primary-hi"
                        >
                            <Phone className="h-5 w-5" />
                        </a>

                        <a
                            href={`mailto:${member.BusinessProfiles[0]?.email || ''}`}
                            aria-label="Email"
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/10 hover:text-primary-hi"
                        >
                            <Mail className="h-5 w-5" />
                        </a>

                        <a
                            href={`https://wa.me/${member.BusinessProfiles[0]?.contact || ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="WhatsApp"
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/10 hover:text-primary-hi"
                        >
                            <MessageCircle className="h-5 w-5" />
                        </a>

                        <button
                            type="button"
                            onClick={handleShare}
                            aria-label="Share"
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/10 hover:text-primary-hi"
                        >
                            <Share2 className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BusinessDetailView;
